import React, { useState, useMemo } from 'react'

// Google Apps Script Web App URL — deploy a script with a doPost(e) handler
// that emails you the submission, then paste the /exec URL here.
// See microsite/CONTACT_FORM_SETUP.md for the script + deployment steps.
const FORM_ENDPOINT = 'https://script.google.com/macros/s/AKfycbwU-RWjdWFK6TR3RhdZONEZu5_N-NsZ8EKYDt7dZK867z9kzgovj4gzhHTSCWMBkpZKLw/exec'

type Status = 'idle' | 'sending' | 'sent' | 'error' | 'wrong-answer'

function randomInt(max: number): number {
  return Math.floor(Math.random() * max) + 1
}

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle')
  // A fresh math question per page load. Sent along with the form (as a+b and
  // the expected sum) so the Apps Script can verify it too — the client check
  // alone only stops honest users who fat-finger the answer, not a bot that
  // skips the page and POSTs straight to the endpoint.
  const [a, b] = useMemo(() => [randomInt(10), randomInt(10)], [])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)

    // Classic hidden-field honeypot: real users never see or fill this input
    // (it's visually hidden), so anything in it means a bot filled every field.
    if ((data.get('company') as string)?.trim()) {
      setStatus('sent') // pretend success — don't tip the bot off
      form.reset()
      return
    }

    const answer = Number(data.get('answer'))
    if (answer !== a + b) {
      setStatus('wrong-answer')
      return
    }

    setStatus('sending')
    data.set('mathCheck', `${a}+${b}=${a + b}`)
    try {
      // Apps Script web apps don't return CORS headers, so the browser can't
      // read the response — 'no-cors' lets the request go through anyway and
      // we just assume success once it doesn't throw.
      await fetch(FORM_ENDPOINT, { method: 'POST', body: data, mode: 'no-cors' })
      setStatus('sent')
      form.reset()
    } catch {
      setStatus('error')
    }
  }

  return (
    <section className="page">
      <div className="container">
        <h1>Contact</h1>
        <p className="updated">Questions, feedback, or a bug to report — we’d like to hear it.</p>

        {status === 'sent' ? (
          <p>Thanks — your message is on its way. We’ll get back to you at the email you provided.</p>
        ) : (
          <form className="contact-form" onSubmit={handleSubmit}>
            <label htmlFor="name">Name</label>
            <input id="name" name="name" type="text" required />

            <label htmlFor="email">Email</label>
            <input id="email" name="email" type="email" required />

            <label htmlFor="message">Message</label>
            <textarea id="message" name="message" rows={6} required />

            {/* Honeypot — hidden from real users (and skipped by screen readers
                via aria-hidden + tabIndex), but a bot that fills every field
                trips it. */}
            <input
              type="text"
              name="company"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hp-field"
            />

            <label htmlFor="answer">
              Quick check: what is {a} + {b}?
            </label>
            <input id="answer" name="answer" type="text" inputMode="numeric" required />

            <button className="btn" type="submit" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending…' : 'Send message'}
            </button>

            {status === 'wrong-answer' && (
              <p className="form-note form-note-error">That answer doesn’t look right — please try again.</p>
            )}
            {status === 'error' && (
              <p className="form-note form-note-error">
                Sorry, something went wrong submitting your form.
              </p>
            )}
          </form>
        )}
      </div>
    </section>
  )
}
